import React, { useState, useEffect } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  BOMItem,
  ComponentCondition,
  ProcurementStatus,
  HollanderInterchangeEntry,
} from '../../types/bom';
import {
  RestorationPhase,
  RESTORATION_PHASES,
  VehicleSystem,
} from '../../types/teardown';
import { calculateComponentVariance } from '../../utils/budgetCalculations';
import { MechanicalButton } from '../common/MechanicalButton';
import { ConditionBadge } from '../common/ConditionBadge';
import { HollanderLookupBar } from './HollanderLookupBar';
import {
  X,
  Layers,
  Save,
  Search,
  Truck,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Hash,
} from 'lucide-react';

export interface AddPartModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialItem?: BOMItem | null;
  initialPhase?: RestorationPhase;
}

const VEHICLE_SYSTEMS: VehicleSystem[] = [
  'chassis',
  'suspension',
  'steering',
  'brakes',
  'engine',
  'transmission',
  'drivetrain',
  'body',
  'paint',
  'electrical',
  'interior',
  'exhaust',
  'cooling',
  'fuel',
  'wheels_tires',
  'glass_trim',
];

const CONDITIONS: ComponentCondition[] = [
  'OEM NOS',
  'Reproduction',
  'Salvage',
  'Remanufactured',
];

const STATUSES: ProcurementStatus[] = ['Needed', 'Sourced', 'Installed'];

export const AddPartModal: React.FC<AddPartModalProps> = ({
  isOpen,
  onClose,
  initialItem,
  initialPhase,
}) => {
  const { state, dispatch } = useRestoMod();
  const { project, subAssemblies, activePhase } = state;

  const [partName, setPartName] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [phase, setPhase] = useState<RestorationPhase>(
    initialPhase || activePhase || 'chassis_and_suspension'
  );
  const [system, setSystem] = useState<VehicleSystem>('suspension');
  const [subAssemblyId, setSubAssemblyId] = useState<string>('');
  const [condition, setCondition] = useState<ComponentCondition>('Reproduction');
  const [status, setStatus] = useState<ProcurementStatus>('Needed');
  const [vendor, setVendor] = useState('');
  const [vendorPartNumber, setVendorPartNumber] = useState('');
  const [orderTrackingNumber, setOrderTrackingNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');
  const [quantity, setQuantity] = useState<number>(1);
  const [unit, setUnit] = useState('ea');
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [actualCost, setActualCost] = useState<number>(0);
  const [shippingCost, setShippingCost] = useState<number>(0);
  const [taxCost, setTaxCost] = useState<number>(0);
  const [coreCharge, setCoreCharge] = useState<number>(0);
  const [coreRefunded, setCoreRefunded] = useState<number>(0);
  const [hollanderNumber, setHollanderNumber] = useState('');
  const [interchangeNotes, setInterchangeNotes] = useState('');
  const [donorVehiclesText, setDonorVehiclesText] = useState('');
  const [notes, setNotes] = useState('');

  const [showHollanderPicker, setShowHollanderPicker] = useState(false);
  const [formError, setFormError] = useState('');

  // Pre-fill fields when editing or opening
  useEffect(() => {
    if (initialItem) {
      setPartName(initialItem.partName || '');
      setPartNumber(initialItem.partNumber || '');
      setPhase(initialItem.phase || 'chassis_and_suspension');
      setSystem(initialItem.system || 'suspension');
      setSubAssemblyId(initialItem.subAssemblyId || '');
      setCondition(initialItem.condition || 'Reproduction');
      setStatus(initialItem.status || 'Needed');
      setVendor(initialItem.vendor || '');
      setVendorPartNumber(initialItem.vendorPartNumber || '');
      setOrderTrackingNumber(initialItem.orderTrackingNumber || '');
      setPurchaseDate(initialItem.purchaseDate || '');
      setReceiptNumber(initialItem.receiptNumber || '');
      setReceiptUrl(initialItem.receiptUrl || '');
      setQuantity(initialItem.quantity || 1);
      setUnit(initialItem.unit || 'ea');
      setEstimatedCost(initialItem.estimatedCost || 0);
      setActualCost(initialItem.actualCost || 0);
      setShippingCost(initialItem.shippingCost || 0);
      setTaxCost(initialItem.taxCost || 0);
      setCoreCharge(initialItem.coreCharge || 0);
      setCoreRefunded(initialItem.coreRefunded || 0);
      setHollanderNumber(initialItem.hollanderNumber || '');
      setInterchangeNotes(initialItem.interchangeNotes || '');
      setDonorVehiclesText(initialItem.donorVehicles?.join('\n') || '');
      setNotes(initialItem.notes || '');
    } else {
      // Defaults for new part
      setPartName('');
      setPartNumber('');
      setPhase(initialPhase || activePhase || 'chassis_and_suspension');
      setSystem('suspension');
      setSubAssemblyId('');
      setCondition('Reproduction');
      setStatus('Needed');
      setVendor('');
      setVendorPartNumber('');
      setOrderTrackingNumber('');
      setPurchaseDate('');
      setReceiptNumber('');
      setReceiptUrl('');
      setQuantity(1);
      setUnit('ea');
      setEstimatedCost(0);
      setActualCost(0);
      setShippingCost(0);
      setTaxCost(0);
      setCoreCharge(0);
      setCoreRefunded(0);
      setHollanderNumber('');
      setInterchangeNotes('');
      setDonorVehiclesText('');
      setNotes('');
    }
    setFormError('');
  }, [initialItem, initialPhase, activePhase, isOpen]);

  // Sub-assemblies matching current phase
  const availableSubAssemblies = subAssemblies.filter((s) => s.phase === phase);

  // Live item variance preview calculation
  const previewItem: BOMItem = {
    id: initialItem?.id || 'preview',
    projectId: project?.id || 'default_proj',
    phase,
    subAssemblyId: subAssemblyId || undefined,
    partNumber,
    partName,
    system,
    condition,
    status,
    vendor,
    vendorPartNumber,
    orderTrackingNumber,
    purchaseDate,
    receiptNumber,
    receiptUrl,
    quantity: Math.max(1, quantity),
    unit,
    estimatedCost,
    actualCost,
    shippingCost,
    taxCost,
    coreCharge,
    coreRefunded,
    hollanderNumber,
    interchangeNotes,
    notes,
    createdAt: initialItem?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const variancePreview = calculateComponentVariance(previewItem);

  const handleApplyHollander = (entry: HollanderInterchangeEntry) => {
    setHollanderNumber(entry.hollanderNumber);
    setInterchangeNotes(entry.interchangeNotes);
    setDonorVehiclesText(entry.compatibleVehicles.join('\n'));
    if (!partName.trim()) {
      setPartName(entry.description);
    }
    setShowHollanderPicker(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!partName.trim()) {
      setFormError('Part Description / Name is required.');
      return;
    }

    const donorVehicles = donorVehiclesText
      .split('\n')
      .map((s) => s.trim().replace(/^•\s*/, ''))
      .filter(Boolean);

    const now = new Date().toISOString();

    const itemPayload: BOMItem = {
      id: initialItem?.id || `bom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      projectId: project?.id || 'proj_camaro_1969',
      phase,
      subAssemblyId: subAssemblyId || undefined,
      partNumber: partNumber.trim(),
      partName: partName.trim(),
      system,
      condition,
      status,
      vendor: vendor.trim(),
      vendorPartNumber: vendorPartNumber.trim() || undefined,
      orderTrackingNumber: orderTrackingNumber.trim() || undefined,
      purchaseDate: purchaseDate.trim() || undefined,
      receiptNumber: receiptNumber.trim() || undefined,
      receiptUrl: receiptUrl.trim() || undefined,
      quantity: Math.max(1, Number(quantity) || 1),
      unit: unit.trim() || 'ea',
      estimatedCost: Math.max(0, Number(estimatedCost) || 0),
      actualCost: Math.max(0, Number(actualCost) || 0),
      shippingCost: Math.max(0, Number(shippingCost) || 0),
      taxCost: Math.max(0, Number(taxCost) || 0),
      coreCharge: Math.max(0, Number(coreCharge) || 0),
      coreRefunded: Math.max(0, Number(coreRefunded) || 0),
      hollanderNumber: hollanderNumber.trim() || undefined,
      interchangeNotes: interchangeNotes.trim() || undefined,
      donorVehicles: donorVehicles.length > 0 ? donorVehicles : undefined,
      notes: notes.trim(),
      createdAt: initialItem?.createdAt || now,
      updatedAt: now,
    };

    if (initialItem) {
      dispatch({ type: 'UPDATE_BOM_ITEM', payload: itemPayload });
    } else {
      dispatch({ type: 'ADD_BOM_ITEM', payload: itemPayload });
    }

    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 overflow-y-auto">
        <div className="bg-shop-panel border-2 border-shop-steel rounded shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden my-auto text-shop-light">
          {/* Modal Header */}
          <div className="px-5 py-3.5 bg-shop-darkest border-b border-shop-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="p-2 bg-shop-panel rounded border border-shop-border text-safety-amber">
                <Layers className="w-5 h-5" />
              </span>
              <div>
                <h3 className="text-base font-bold text-shop-highlight uppercase tracking-wider">
                  {initialItem ? 'Edit BOM Line Item' : 'Add BOM Component & Sourcing Record'}
                </h3>
                <p className="text-xs text-shop-muted">
                  Procurement matrix, condition provenance, budget variance, and Hollander cross-reference
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded bg-shop-dark border border-shop-border text-shop-muted hover:text-shop-highlight hover:border-shop-steel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body / Form */}
          <form onSubmit={handleSubmit} className="p-5 overflow-y-auto flex-1 space-y-6">
            {formError && (
              <div className="p-3 bg-[#7f1d1d]/80 border border-safety-red rounded text-xs font-bold text-white flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            {/* SECTION 1: CORE PART IDENTIFIERS */}
            <div className="space-y-4">
              <h4 className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider border-b border-shop-border pb-1">
                1. Component Identification
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Part Name / Description */}
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-light uppercase flex items-center justify-between">
                    <span>Part Description *</span>
                    <span className="text-[10px] text-shop-muted">e.g. Front Upper Ball Joint</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={partName}
                    onChange={(e) => setPartName(e.target.value)}
                    placeholder="e.g. Quick-Ratio Power Steering Gearbox"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-sm text-shop-highlight font-sans focus:outline-none focus:border-safety-amber"
                  />
                </div>

                {/* OEM / Manufacturer Part Number */}
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-light uppercase flex items-center justify-between">
                    <span>OEM / Mfr Part #</span>
                    <span className="text-[10px] text-shop-muted">Stamping or catalog #</span>
                  </label>
                  <input
                    type="text"
                    value={partNumber}
                    onChange={(e) => setPartNumber(e.target.value)}
                    placeholder="e.g. GM 7806396 / MOOG-K6136"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-sm text-shop-highlight font-mono focus:outline-none focus:border-safety-amber"
                  />
                </div>
              </div>

              {/* Phase, System & SubAssembly Row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Restoration Phase */}
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Restoration Phase
                  </label>
                  <select
                    value={phase}
                    onChange={(e) => setPhase(e.target.value as RestorationPhase)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber cursor-pointer"
                  >
                    {(Object.keys(RESTORATION_PHASES) as RestorationPhase[]).map((pKey) => (
                      <option key={pKey} value={pKey}>
                        {RESTORATION_PHASES[pKey].sequence}. {RESTORATION_PHASES[pKey].title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle System */}
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Vehicle System
                  </label>
                  <select
                    value={system}
                    onChange={(e) => setSystem(e.target.value as VehicleSystem)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber cursor-pointer"
                  >
                    {VEHICLE_SYSTEMS.map((sys) => (
                      <option key={sys} value={sys}>
                        {sys.toUpperCase().replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* SubAssembly Bin Link */}
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Sub-Assembly Storage Bin
                  </label>
                  <select
                    value={subAssemblyId}
                    onChange={(e) => setSubAssemblyId(e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber cursor-pointer"
                  >
                    <option value="">(No Storage Bin Linked)</option>
                    {availableSubAssemblies.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.binNumber} - {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* SECTION 2: CONDITION & PROCUREMENT STATUS */}
            <div className="space-y-4">
              <h4 className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider border-b border-shop-border pb-1">
                2. Condition &amp; Sourcing Status
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Condition Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase block">
                    Component Condition (4 Mandatory Categories)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {CONDITIONS.map((cond) => (
                      <button
                        type="button"
                        key={cond}
                        onClick={() => setCondition(cond)}
                        className={`min-h-[44px] px-3 py-2 rounded border text-xs font-mono font-bold text-left flex items-center justify-between transition-all cursor-pointer ${
                          condition === cond
                            ? 'bg-shop-darkest border-safety-amber text-shop-highlight shadow-[inset_0_2px_0_0_#f59e0b]'
                            : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well'
                        }`}
                      >
                        <ConditionBadge condition={cond} size="sm" />
                        {condition === cond && <CheckCircle2 className="w-4 h-4 text-safety-amber" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sourcing Status Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase block">
                    Procurement Lifecycle Status
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {STATUSES.map((st) => (
                      <button
                        type="button"
                        key={st}
                        onClick={() => setStatus(st)}
                        className={`min-h-[44px] px-2 py-2 rounded border text-xs font-mono font-bold text-center flex flex-col items-center justify-center transition-all cursor-pointer ${
                          status === st
                            ? 'bg-shop-darkest border-safety-amber text-shop-highlight shadow-[inset_0_2px_0_0_#f59e0b]'
                            : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well'
                        }`}
                      >
                        <ConditionBadge status={st} size="sm" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 3: VENDOR, RECEIPT & AUDIT DATA */}
            <div className="space-y-4">
              <h4 className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider border-b border-shop-border pb-1">
                3. Vendor, Invoicing &amp; Audit Trail
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Supplier / Vendor
                  </label>
                  <input
                    type="text"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    placeholder="e.g. Summit Racing / YearOne"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light font-mono focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Invoice / Receipt #
                  </label>
                  <input
                    type="text"
                    value={receiptNumber}
                    onChange={(e) => setReceiptNumber(e.target.value)}
                    placeholder="e.g. INV-98241"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light font-mono focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Purchase Date
                  </label>
                  <input
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light font-mono focus:outline-none focus:border-safety-amber"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Tracking # / Carrier
                  </label>
                  <input
                    type="text"
                    value={orderTrackingNumber}
                    onChange={(e) => setOrderTrackingNumber(e.target.value)}
                    placeholder="e.g. 1Z9999999999999999 (UPS)"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light font-mono focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Receipt Document / Photo Link
                  </label>
                  <input
                    type="text"
                    value={receiptUrl}
                    onChange={(e) => setReceiptUrl(e.target.value)}
                    placeholder="e.g. /receipts/inv-98241.pdf or photo key"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light font-mono focus:outline-none focus:border-safety-amber"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 4: FINANCIAL ACCOUNTING & VARIANCE ENGINE */}
            <div className="space-y-4">
              <h4 className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider border-b border-shop-border pb-1">
                4. Line-Item Costing &amp; Variance Engine
              </h4>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-sm font-mono text-shop-highlight focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder="ea, pair, set"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Budgeted Unit Cost ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={estimatedCost}
                    onChange={(e) => setEstimatedCost(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-sm font-mono text-shop-highlight focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-safety-amber uppercase">
                    Actual Unit Cost ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={actualCost}
                    onChange={(e) => setActualCost(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-safety-amber rounded text-sm font-mono text-safety-amber focus:outline-none focus:border-safety-amber"
                  />
                </div>
              </div>

              {/* Extra costs: Shipping, Tax, Core Charge, Core Refund */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase flex items-center gap-1">
                    <Truck className="w-3.5 h-3.5" /> Shipping ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={shippingCost}
                    onChange={(e) => setShippingCost(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Sales Tax ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={taxCost}
                    onChange={(e) => setTaxCost(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Core Deposit ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={coreCharge}
                    onChange={(e) => setCoreCharge(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-safety-green uppercase flex items-center gap-1">
                    <RotateCcw className="w-3.5 h-3.5" /> Core Refunded ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={coreRefunded}
                    onChange={(e) => setCoreRefunded(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-safety-green focus:outline-none focus:border-safety-amber"
                  />
                </div>
              </div>

              {/* Dynamic Live Cost Rollup Box */}
              <div className="p-3 bg-shop-darkest border border-shop-border rounded flex items-center justify-between flex-wrap gap-4 font-mono text-xs">
                <div>
                  <span className="text-shop-muted text-[10px] uppercase block">Base Estimated</span>
                  <span className="text-shop-highlight font-bold text-sm">
                    ${variancePreview.baseEstimatedCost.toFixed(2)}
                  </span>
                </div>

                <div>
                  <span className="text-shop-muted text-[10px] uppercase block">
                    Net Actual (inc. ship/tax/cores)
                  </span>
                  <span className="text-safety-amber font-bold text-sm">
                    ${variancePreview.netActualCost.toFixed(2)}
                  </span>
                </div>

                <div>
                  <span className="text-shop-muted text-[10px] uppercase block">Projected Variance</span>
                  <span
                    className={`font-bold text-sm ${
                      variancePreview.isFavorable ? 'text-safety-green' : 'text-safety-red'
                    }`}
                  >
                    {variancePreview.isFavorable ? '-' : '+'}
                    ${Math.abs(variancePreview.costVariance).toFixed(2)} ({variancePreview.variancePercentage.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>

            {/* SECTION 5: HOLLANDER INTERCHANGE & DONOR VEHICLES */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-shop-border pb-1">
                <h4 className="text-xs font-mono font-bold text-safety-amber uppercase tracking-wider">
                  5. Hollander Interchange &amp; Donor Vehicles
                </h4>
                <MechanicalButton
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={<Search className="w-3.5 h-3.5 text-safety-amber" />}
                  onClick={() => setShowHollanderPicker(true)}
                >
                  Lookup In Directory
                </MechanicalButton>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5" /> Hollander Interchange #
                  </label>
                  <input
                    type="text"
                    value={hollanderNumber}
                    onChange={(e) => setHollanderNumber(e.target.value)}
                    placeholder="e.g. 560-00431"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-safety-amber font-mono font-bold focus:outline-none focus:border-safety-amber"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                    Interchange Compatibility Notes
                  </label>
                  <input
                    type="text"
                    value={interchangeNotes}
                    onChange={(e) => setInterchangeNotes(e.target.value)}
                    placeholder="e.g. Requires metric O-ring adapters on early lines"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light font-mono focus:outline-none focus:border-safety-amber"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                  Compatible Donor Vehicles (One per line)
                </label>
                <textarea
                  rows={2}
                  value={donorVehiclesText}
                  onChange={(e) => setDonorVehiclesText(e.target.value)}
                  placeholder="1968-1972 Chevelle / Monte Carlo&#10;1970-1981 Camaro&#10;1969-1974 Nova"
                  className="w-full p-2.5 bg-shop-darkest border border-shop-border rounded text-xs font-mono text-shop-light focus:outline-none focus:border-safety-amber"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-mono font-bold text-shop-muted uppercase">
                  Technician / Shop Installation Notes
                </label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Installation notes, torque specs, hardware requirements, or condition notes..."
                  className="w-full p-2.5 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light focus:outline-none focus:border-safety-amber font-sans"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="pt-4 border-t border-shop-border flex items-center justify-between gap-3 flex-wrap">
              <MechanicalButton type="button" variant="outline" size="sm" onClick={onClose}>
                Cancel
              </MechanicalButton>

              <MechanicalButton
                type="submit"
                variant="primary"
                size="default"
                icon={<Save className="w-4 h-4" />}
              >
                {initialItem ? 'Save BOM Changes' : 'Add Component to Matrix'}
              </MechanicalButton>
            </div>
          </form>
        </div>
      </div>

      {/* Embedded Hollander Picker Modal */}
      {showHollanderPicker && (
        <HollanderLookupBar
          isOpen={showHollanderPicker}
          onClose={() => setShowHollanderPicker(false)}
          onSelectEntry={handleApplyHollander}
        />
      )}
    </>
  );
};
